/* ── PAG Insights constants ────────────────────────────────────────────
   Types, label look-ups and chart-sizing constants that back the
   PrivilegeAccessInsights page.  Keep runtime helpers in pagHelpers;
   this file is pure data. */

import type { PillTone } from "./pagConstants";

/* ── Domain types ──────────────────────────────────────────────────── */

export type AccountType =
  | "DOMAIN_ADMIN"
  | "ENTERPRISE_ADMIN"
  | "SCHEMA_ADMIN"
  | "SERVER_ADMIN"
  | "SUDO_ROOT"
  | "SERVICE_ACCOUNT"
  | "LOCAL_ADMIN";

export type PrivilegeBasis =
  | "GROUP_MEMBERSHIP"
  | "DIRECT_ID"
  | "SUDO_RULE"
  | "SERVICE_PRINCIPAL"
  | "LOCAL_ADMIN_GROUP";

export type InsightsRiskLevel = "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";
export type PamStatus = "VAULTED" | "NOT_VAULTED";

/* ── Label maps ────────────────────────────────────────────────────── */

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  DOMAIN_ADMIN: "Domain Admin",
  ENTERPRISE_ADMIN: "Enterprise Admin",
  SCHEMA_ADMIN: "Schema Admin",
  SERVER_ADMIN: "Server Admin",
  SUDO_ROOT: "Sudo / Root",
  SERVICE_ACCOUNT: "Service Account",
  LOCAL_ADMIN: "Local Admin",
};

export const PRIVILEGE_BASIS_LABELS: Record<PrivilegeBasis, string> = {
  GROUP_MEMBERSHIP: "Group Membership",
  DIRECT_ID: "Direct ID",
  SUDO_RULE: "Sudo Rule",
  SERVICE_PRINCIPAL: "Service Principal",
  LOCAL_ADMIN_GROUP: "Local Admin Group",
};

export const RISK_LABELS: Record<InsightsRiskLevel, string> = {
  CRITICAL: "Critical",
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
};

export const PAM_STATUS_LABELS: Record<PamStatus, string> = {
  VAULTED: "Vaulted",
  NOT_VAULTED: "Not Vaulted",
};

/* ── Pill tone maps ────────────────────────────────────────────────── */

export const RISK_PILL_TONES: Record<string, PillTone> = {
  CRITICAL: "red",
  HIGH: "orange",
  MEDIUM: "amber",
  LOW: "cyan",
  UNCATEGORISED: "slate",
};

export const RISK_TONES: Record<InsightsRiskLevel, PillTone> = {
  CRITICAL: "red",
  HIGH: "orange",
  MEDIUM: "amber",
  LOW: "cyan",
};

export const PAM_TONES: Record<PamStatus, PillTone> = {
  VAULTED: "emerald",
  NOT_VAULTED: "red",
};

/* ── Chart datum ───────────────────────────────────────────────────── */

export type ChartDatum = {
  name: string;
  value: number;
  color?: string;
  /** Raw value the list API filters on when this bar or slice is clicked. */
  key?: string;
  sourceSystemIds?: number[];
};

export const chartTotal = (data: ChartDatum[]) => data.reduce((sum, r) => sum + (r?.value ?? 0), 0);
export const chartRanked = (data: ChartDatum[]) => [...data].sort((a, b) => b.value - a.value);

/* ── Spike chart sizing ────────────────────────────────────────────── */

/** Minimum horizontal room one category needs before its label crowds. */
export const SPIKE_SLOT_WIDTH = 84;
/** Width of one character of a tick label: 11px semibold, measured. */
export const SPIKE_LABEL_CHAR = 6.4;
/** Clear space kept between one label and the next. */
export const SPIKE_LABEL_GUTTER = 18;
